import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Check, LoaderCircle } from "lucide-react";
import { forwardRef, type ButtonHTMLAttributes, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "../../lib/cn";

const buttonVariants = cva("ui-button", {
  variants: {
    variant: {
      primary: "ui-button-primary",
      secondary: "ui-button-secondary",
      ghost: "ui-button-ghost",
      danger: "ui-button-danger",
      "danger-outline": "ui-button-danger-outline",
      tool: "ui-button-tool"
    },
    size: {
      default: "ui-button-default",
      compact: "ui-button-compact",
      icon: "ui-button-icon ui-button-icon-size"
    },
    full: {
      true: "ui-button-full"
    },
    active: {
      true: "ui-button-active"
    }
  },
  defaultVariants: {
    variant: "secondary",
    size: "default"
  }
});

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  loadingLabel?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      active,
      asChild = false,
      children,
      className,
      disabled,
      full,
      loading = false,
      loadingLabel,
      size,
      variant,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    const loadingProps = loading && !asChild
      ? { "aria-busy": true, disabled: true }
      : { disabled };

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, full, active }), className)}
        ref={ref}
        {...loadingProps}
        {...props}
      >
        {loading && !asChild ? (
          <>
            <LoaderCircle className="ui-button-spinner" size={18} aria-hidden="true" />
            {loadingLabel ?? children}
          </>
        ) : children}
      </Comp>
    );
  }
);

Button.displayName = "Button";

export interface ChipButtonProps
  extends Omit<ButtonProps, "variant"> {
  selected?: boolean;
}

export function ChipButton({ children, className, selected = false, ...props }: ChipButtonProps) {
  return (
    <Button
      variant="tool"
      className={cn("ui-button-chip", selected && "ui-button-chip-selected", className)}
      aria-pressed={selected}
      {...props}
    >
      {selected ? <Check className="ui-button-chip-check" size={16} aria-hidden="true" /> : null}
      {children}
    </Button>
  );
}

export interface StatusButtonProps
  extends Omit<ButtonProps, "children" | "title" | "variant"> {
  badge: ReactNode;
  detail?: ReactNode;
  statusTone?: "default" | "attention" | "clear";
  title: ReactNode;
}

export const StatusButton = forwardRef<HTMLButtonElement, StatusButtonProps>(
  ({ badge, detail, statusTone = "default", title, className, ...props }, ref) => (
    <Button
      ref={ref}
      variant="tool"
      className={cn("ui-button-status", `ui-button-status-${statusTone}`, className)}
      {...props}
    >
      <span className="ui-button-status-badge">{badge}</span>
      <span className="ui-button-status-copy">
        <strong>{title}</strong>
        {detail && <small>{detail}</small>}
      </span>
    </Button>
  )
);

StatusButton.displayName = "StatusButton";

export function ButtonCountBadge({ className, ...props }: HTMLAttributes<HTMLSpanElement>) {
  return <span className={cn("ui-button-count-badge", className)} {...props} />;
}

export interface SwatchButtonProps
  extends Omit<ButtonProps, "children" | "variant" | "size"> {
  label: string;
  selected?: boolean;
  swatch: string;
}

export function SwatchButton({
  className,
  label,
  selected = false,
  swatch,
  style,
  ...props
}: SwatchButtonProps) {
  return (
    <Button
      variant="tool"
      className={cn(
        "ui-button-chip",
        "ui-swatch-button",
        selected && "ui-swatch-button-selected",
        swatch === "transparent" && "ui-swatch-button-transparent",
        className
      )}
      style={{ "--swatch-color": swatch, ...style } as CSSProperties}
      aria-pressed={selected}
      {...props}
    >
      <span className="ui-swatch-button-dot" aria-hidden="true" />
      <span>{label}</span>
    </Button>
  );
}

export interface ThumbnailButtonProps
  extends Omit<ButtonProps, "children" | "variant" | "size"> {
  children?: ButtonProps["children"];
  imageSrc?: string;
  imageStyle?: CSSProperties;
  label: string;
  selected?: boolean;
}

export function ThumbnailButton({
  children,
  className,
  imageSrc,
  imageStyle,
  label,
  selected,
  ...props
}: ThumbnailButtonProps) {
  return (
    <Button
      variant="tool"
      className={cn("ui-thumbnail-button", selected && "ui-thumbnail-button-selected", className)}
      aria-label={label}
      aria-pressed={selected === undefined ? undefined : selected}
      {...props}
    >
      {imageSrc ? <img src={imageSrc} alt="" style={imageStyle} /> : children}
    </Button>
  );
}
